import { useEffect, useMemo, useRef, useState, Fragment } from "react";
import { Navigation } from "@/components/Navigation";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChevronDown, Plus, Save, X, ArrowLeft, Search } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

interface SlotBook {
  id: string;
  title: string;
  isbn: string;
  price: number;
  quantity: number;
}

const MESES = [
  "Janeiro",
  "Fevereiro",
  "Março",
  "Abril",
  "Maio",
  "Junho",
  "Julho",
  "Agosto",
  "Setembro",
  "Outubro",
  "Novembro",
  "Dezembro",
];

const CATEGORY_ROWS: Array<{ code: string; kind: "Primária" | "Secundária" }> = [
  { code: "A1", kind: "Primária" },
  { code: "A1", kind: "Secundária" },
  { code: "A2", kind: "Primária" },
  { code: "A2", kind: "Secundária" },
  { code: "B1", kind: "Primária" },
  { code: "B1", kind: "Secundária" },
  { code: "B2", kind: "Primária" },
  { code: "B2", kind: "Secundária" },
  { code: "C", kind: "Primária" },
  { code: "C", kind: "Secundária" },
  { code: "D", kind: "Primária" },
  { code: "D", kind: "Secundária" },
  { code: "E", kind: "Primária" },
  { code: "E", kind: "Secundária" },
];

const MOCK_BOOKS: SlotBook[] = [
  { id: "1", title: "Mr. Lido Bem", isbn: "9788595036079", price: 10000, quantity: 15000 },
  { id: "2", title: "Chapeuzinho Amarelo", isbn: "9788635222265", price: 12000, quantity: 12000 },
  { id: "3", title: "Cindy veste da Montaria", isbn: "9788651656701", price: 14000, quantity: 10000 },
  { id: "4", title: "A Casa da Árvore com 13 Andares", isbn: "9788635607667", price: 28000, quantity: 8000 },
  { id: "5", title: "O Grúfalo", isbn: "9788654321098", price: 15000, quantity: 20000 },
  { id: "6", title: "Onde Vivem os Monstros", isbn: "9788574062907", price: 18000, quantity: 18000 },
  { id: "7", title: "O Pequeno Príncipe", isbn: "9788522008315", price: 22000, quantity: 25000 },
  { id: "8", title: "A Lagarta Muito Comilona", isbn: "9788574064000", price: 16500, quantity: 14000 },
];

const SIMULATION_PRESETS: Record<string, { r: number; c: number; books: SlotBook[] }[]> = {
  "1": [
    { r: 0, c: 0, books: [MOCK_BOOKS[0], MOCK_BOOKS[1]] },
    { r: 2, c: 1, books: [MOCK_BOOKS[2]] },
    { r: 4, c: 2, books: [MOCK_BOOKS[3], MOCK_BOOKS[4], MOCK_BOOKS[5]] },
  ],
  "2": [
    { r: 1, c: 3, books: [MOCK_BOOKS[0]] },
    { r: 3, c: 3, books: [MOCK_BOOKS[1], MOCK_BOOKS[2]] },
  ],
};

const SIMULATION_NAMES: Record<string, string> = {
  "1": "Simulação Q1 2024",
  "2": "Simulação Q2 2024",
  "3": "Simulação Backlist 2024",
  "4": "Simulação Campanha Dia das Crianças",
  "5": "Simulação Piloto 2025",
  "6": "Simulação Q1 2025",
};

export default function Simulation() {
  const navigate = useNavigate();
  const [version, setVersion] = useState(1);
  const year = new Date().getFullYear();
  const [status, setStatus] = useState<"Ativo" | "Arquivado" | "Rascunho">("Rascunho");
  const [isFinalVersion, setIsFinalVersion] = useState(false);

  const searchParams = new URLSearchParams(window.location.search);
  const simulationId = searchParams.get("id") || "";
  const simulationName = SIMULATION_NAMES[simulationId] || "Nova Simulação";

  const initialMatrix = useMemo(() => {
    const base = CATEGORY_ROWS.map(() => MESES.map(() => [] as SlotBook[]));
    if (simulationId && SIMULATION_PRESETS[simulationId]) {
      const preset = SIMULATION_PRESETS[simulationId];
      preset.forEach(({ r, c, books }) => {
        if (base[r] && base[r][c]) base[r][c] = [...books];
      });
    }
    return base;
  }, [simulationId]);

  const [matrix, setMatrix] = useState<SlotBook[][][]>(() => initialMatrix);
  const [selectedSlot, setSelectedSlot] = useState<{ r: number; c: number } | null>(null);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    setMatrix(initialMatrix);
  }, [initialMatrix]);

  const currentSlotBooks = selectedSlot && matrix[selectedSlot.r]?.[selectedSlot.c] ? matrix[selectedSlot.r][selectedSlot.c] : [];
  const canAddMore = currentSlotBooks.length < 5;

  const filteredBooks = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return MOCK_BOOKS;
    return MOCK_BOOKS.filter(
      (book) =>
        book.title.toLowerCase().includes(q) ||
        book.isbn.includes(q)
    );
  }, [searchQuery]);

  const addBookToSlot = (r: number, c: number, book: SlotBook) => {
    setMatrix((prev) => {
      const next = prev.map((row) => row.map((cell) => [...cell]));
      if (next[r]?.[c] && next[r][c].length < 5 && !next[r][c].find((b) => b.id === book.id)) {
        next[r][c] = [...next[r][c], book];
      }
      return next;
    });
  };

  const removeBookFromSlot = (r: number, c: number, bookId: string) => {
    setMatrix((prev) => {
      const next = prev.map((row) => row.map((cell) => [...cell]));
      if (next[r]?.[c]) {
        next[r][c] = next[r][c].filter((b) => b.id !== bookId);
      }
      return next;
    });
  };

  const clearSlot = (r: number, c: number) => {
    setMatrix((prev) => {
      const next = prev.map((row) => row.map((cell) => [...cell]));
      if (next[r]?.[c] !== undefined) {
        next[r][c] = [];
      }
      return next;
    });
  };

  const handleSaveVersion = () => {
    if (isFinalVersion) {
      toast.error("Não é possível salvar nova versão. Esta é a versão final.");
      return;
    }
    setVersion((v) => v + 1);
    toast.success(`Versão ${version + 1} salva${status === "Rascunho" ? " como rascunho" : ""}`);
  };

  const handleSaveSimulation = () => {
    if (status === "Rascunho" && !isFinalVersion) {
      toast.success("Rascunho salvo");
    } else if (isFinalVersion) {
      toast.success("Versão final salva");
    } else {
      toast.success("Simulação salva");
    }
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
      minimumFractionDigits: 2,
    }).format(price / 100);
  };

  const formatQuantity = (quantity: number) => {
    return new Intl.NumberFormat("pt-BR").format(quantity) + " un.";
  };

  return (
    <div className="flex h-screen bg-white">
      <Navigation />

      <main className="flex-1 flex flex-col overflow-hidden">
        <div className="sticky top-0 z-50 border-b border-[#BAC9CA] bg-white">
          <div className="flex items-center justify-between px-5 py-5">
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => navigate("/simulacoes")}
                  className="w-12 h-12 rounded-full hover:bg-[#0000000D]"
                >
                  <ArrowLeft className="w-6 h-6 text-[#151C1D]" />
                </Button>
                <h1 className="text-[28px] leading-9 font-medium text-[#151C1D]">
                  Simulação
                </h1>
              </div>
              <div className="ml-14">
                <span className="text-base text-[#3B494A]">{simulationName}</span>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Select value={status} onValueChange={(v: any) => setStatus(v)}>
                <SelectTrigger className="h-12 w-[160px] rounded-xl border-[#BAC9CA]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Rascunho">Rascunho</SelectItem>
                  <SelectItem value="Ativo">Ativo</SelectItem>
                  <SelectItem value="Arquivado">Arquivado</SelectItem>
                </SelectContent>
              </Select>
              <div className="flex items-center gap-2 px-3">
                <span className="text-sm font-medium text-[#3B494A]">Versão Final</span>
                <Switch
                  checked={isFinalVersion}
                  onCheckedChange={setIsFinalVersion}
                />
              </div>
              <Button
                variant="secondary"
                onClick={handleSaveVersion}
                disabled={isFinalVersion}
                className="h-12 px-4 rounded-xl bg-[#EDF4F5] text-[#00646B] hover:bg-[#E0F2F1] disabled:opacity-50"
              >
                <Save className="w-5 h-5 mr-2" />
                Salvar versão
                <span className="ml-2 text-[#3B494A] text-xs">v{version}</span>
              </Button>
              <Button onClick={handleSaveSimulation} className="h-12 px-4 rounded-xl bg-[#00646B] hover:bg-[#00646B]/90 text-white">
                Salvar simulação
              </Button>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-auto px-5 py-9">
          <div
            className="min-w-max grid gap-3"
            style={{ gridTemplateColumns: `200px repeat(${MESES.length}, 280px)` }}
          >
            <div className="h-12 sticky top-0 left-0 z-50 bg-transparent" />
            {MESES.map((m) => (
              <div key={m} className="h-12 px-4 flex items-center justify-center rounded-xl bg-[#EDF4F5] border border-[#BAC9CA] sticky top-0 z-40">
                <span className="text-sm font-medium text-[#151C1D] text-center w-full">{m} {year}</span>
              </div>
            ))}

            {CATEGORY_ROWS.map((row, rIdx) => {
              const isNewCategory = rIdx > 0 && row.kind === "Primária";
              return (
                <Fragment key={`row-${rIdx}`}>
                  {isNewCategory && (
                    <div className="col-span-full h-0 border-t-2 border-dashed border-[#D6E5E6] my-3" />
                  )}
                  <div key={`label-${rIdx}`} className="h-[88px] rounded-xl border border-[#BAC9CA] bg-[#E0F2F1] flex flex-col items-center justify-center px-3 py-2 sticky left-0 z-30">
                    <span className="text-sm font-semibold text-[#135045] leading-none">{row.code}</span>
                    <span className="text-[11px] text-[#3B494A] mt-1">{row.kind}</span>
                  </div>
                  {MESES.map((m, cIdx) => {
                    const items = matrix[rIdx]?.[cIdx] || [];
                    const empty = items.length === 0;
                    const count = items.length;
                    const totalQuantity = items.reduce((sum, book) => sum + book.quantity, 0);
                    return (
                      <div key={`${rIdx}-${m}`} className={`min-h-[88px] rounded-xl border ${empty ? "border-dashed border-[#BAC9CA] bg-[#F3FAFB]" : "border-[#BAC9CA] bg-white"} p-3`}>
                        {empty ? (
                          <button
                            onClick={() => setSelectedSlot({ r: rIdx, c: cIdx })}
                            className="flex items-center justify-center text-[#3B494A] hover:opacity-80 w-full h-full min-h-[64px]"
                          >
                            <Plus className="w-8 h-8" />
                          </button>
                        ) : (
                          <button
                            onClick={() => setSelectedSlot({ r: rIdx, c: cIdx })}
                            className="w-full h-full text-left hover:opacity-80 transition-opacity"
                          >
                            <div className="flex flex-col gap-2">
                              <div className="flex items-start justify-between gap-2">
                                <div className="flex-1 min-w-0 flex flex-col gap-1">
                                  {items.slice(0, 2).map((book, idx) => (
                                    <div key={book.id} className="text-xs text-[#151C1D] truncate">
                                      {idx + 1}. {book.title}
                                    </div>
                                  ))}
                                </div>
                                <div className="flex-shrink-0 flex items-center gap-0.5 bg-[#00646B] text-white px-2 py-0.5 rounded text-xs font-semibold">
                                  <span>{count}</span>
                                  <span>/5</span>
                                </div>
                              </div>
                              <div className="text-xs font-semibold text-[#151C1D] pt-1 border-t border-[#BAC9CA]">
                                {formatQuantity(totalQuantity)}
                              </div>
                            </div>
                          </button>
                        )}
                      </div>
                    );
                  })}
                </Fragment>
              );
            })}
          </div>
        </div>
      </main>

      <Sheet open={selectedSlot !== null} onOpenChange={(open) => !open && setSelectedSlot(null)}>
        <SheetContent className="w-[480px] sm:max-w-[480px] bg-white">
          <SheetHeader>
            <SheetTitle className="text-2xl font-medium text-[#151C1D]">
              {currentSlotBooks.length > 0 ? "Editar Slot" : "Alocar Manualmente"}
            </SheetTitle>
            <p className="text-sm text-[#3B494A]">
              {currentSlotBooks.length > 0
                ? `Gerencie os livros alocados (até ${currentSlotBooks.length}/5)`
                : "Selecione um livro para alocar neste slot"}
            </p>
          </SheetHeader>

          <div className="mt-6 flex flex-col gap-6">
            {currentSlotBooks.length > 0 && (
              <div className="flex flex-col gap-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-[#151C1D]">Livros Alocados</h3>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => selectedSlot && clearSlot(selectedSlot.r, selectedSlot.c)}
                    className="text-xs text-[#93000A] hover:text-[#93000A] hover:bg-[#FFDAD6]"
                  >
                    Excluir Todas Alocações
                  </Button>
                </div>
                <div className="flex flex-col gap-2">
                  {currentSlotBooks.map((book, idx) => (
                    <div key={book.id} className="flex items-center gap-3 p-3 rounded-lg bg-[#F3FAFB] border border-[#BAC9CA]">
                      <div className="flex items-center justify-center w-8 h-8 rounded-full bg-[#D6E5E6] text-[#135045] font-semibold text-sm">
                        {idx + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-[#151C1D] truncate">{book.title}</p>
                        <p className="text-xs text-[#3B494A]">ISBN: {book.isbn}</p>
                        <p className="text-xs text-[#3B494A]">{formatQuantity(book.quantity)}</p>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => selectedSlot && removeBookFromSlot(selectedSlot.r, selectedSlot.c, book.id)}
                        className="flex-shrink-0 h-8 w-8 rounded-lg hover:bg-[#FFDAD6]"
                      >
                        <X className="w-4 h-4 text-[#93000A]" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {canAddMore && (
              <div className="flex flex-col gap-3">
                <h3 className="text-sm font-semibold text-[#151C1D]">
                  {currentSlotBooks.length > 0 ? "Adicionar Livro (2/5)" : "Buscar Livro"}
                </h3>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#3B494A]" />
                  <Input
                    type="text"
                    placeholder="Digite o título ou ISBN..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="h-12 pl-10 pr-4 rounded-xl border-[#BAC9CA] bg-[#F3FAFB]"
                  />
                </div>
                <div className="flex flex-col gap-2 max-h-[400px] overflow-y-auto">
                  {filteredBooks.map((book) => {
                    const isAllocated = currentSlotBooks.find((b) => b.id === book.id);
                    return (
                      <button
                        key={book.id}
                        onClick={() => {
                          if (!isAllocated && selectedSlot) {
                            addBookToSlot(selectedSlot.r, selectedSlot.c, book);
                          }
                        }}
                        disabled={!!isAllocated}
                        className={`flex items-start gap-3 p-3 rounded-lg border text-left transition-colors ${
                          isAllocated
                            ? "bg-[#E8EFF0] border-[#BAC9CA] opacity-50 cursor-not-allowed"
                            : "bg-white border-[#BAC9CA] hover:bg-[#F3FAFB]"
                        }`}
                      >
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-[#151C1D]">{book.title}</p>
                          <p className="text-xs text-[#3B494A]">ISBN: {book.isbn}</p>
                          <p className="text-xs text-[#3B494A]">{formatQuantity(book.quantity)}</p>
                        </div>
                        {isAllocated && (
                          <span className="flex-shrink-0 text-xs text-[#135045] bg-[#B4EEDF] px-2 py-1 rounded">
                            Alocado
                          </span>
                        )}
                      </button>
                    );
                  })}
                  {filteredBooks.length === 0 && (
                    <div className="py-10 text-center text-[#3B494A]">
                      Nenhum livro encontrado
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="flex gap-3 pt-4 border-t border-[#BAC9CA]">
              <Button
                variant="outline"
                onClick={() => setSelectedSlot(null)}
                className="flex-1 h-12 rounded-xl border-[#BAC9CA] text-[#00646B]"
              >
                Cancelar
              </Button>
              <Button
                onClick={() => {
                  toast.success("Alocações salvas");
                  setSelectedSlot(null);
                }}
                className="flex-1 h-12 rounded-xl bg-[#00646B] hover:bg-[#00646B]/90 text-white"
              >
                Confirmar Alocação
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
